/**
 * Stripe.js wrapper.
 */

import { STRIPE_KEY } from "@/config/constants";

let stripePromise: Promise<import("@stripe/stripe-js").Stripe | null> | null =
  null;

/**
 * True when `value` has the methods @stripe/react-stripe-js's own internal
 * isStripe() duck-types to accept an Elements `stripe` prop. A privacy
 * extension that blocks Stripe's script sometimes substitutes a stub
 * `window.Stripe` (so the page doesn't visibly 404) that `loadStripe()`
 * resolves as a non-null value missing these methods; passed through
 * unchecked, react-stripe-js's own validator throws inside an unawaited
 * `.then()` (SPEAK-CLIENT-QB), leaving <Elements> stuck with stripe=null
 * forever instead of the "payment form unavailable" fallback below.
 */
function isUsableStripeInstance(
  value: unknown,
): value is import("@stripe/stripe-js").Stripe {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as Record<string, unknown>).elements === "function" &&
    typeof (value as Record<string, unknown>).createToken === "function" &&
    typeof (value as Record<string, unknown>).createPaymentMethod === "function" &&
    typeof (value as Record<string, unknown>).confirmCardPayment === "function"
  );
}

/**
 * Returns the cached Stripe promise synchronously, initialising it on first call.
 * Required by the <Elements stripe={...}> prop from @stripe/react-stripe-js,
 * which accepts Promise<Stripe | null> rather than an awaited Stripe instance.
 */
export function getStripePromise(): Promise<import("@stripe/stripe-js").Stripe | null> {
  if (!STRIPE_KEY) {
    console.warn("[Stripe] NEXT_PUBLIC_STRIPE_KEY is not set, payment form unavailable");
    return Promise.resolve(null);
  }
  if (!stripePromise) {
    // Initialise synchronously so the promise is available immediately to the Elements provider.
    // Using a dynamic import to avoid loading stripe-js at module parse time (lazy load intent).
    stripePromise = import("@stripe/stripe-js")
      .then((mod) => mod.loadStripe(STRIPE_KEY))
      .then((stripe) => (isUsableStripeInstance(stripe) ? stripe : null))
      .catch((error) => {
        // Resolve to null, never reject: a rejected promise crashes Elements and would be cached forever.
        console.warn("[Stripe] stripe-js failed to load, payment form unavailable", error);
        stripePromise = null;
        return null;
      });
  }
  return stripePromise;
}

/**
 * Async accessor for callers that need to await the Stripe instance (e.g. confirmStripePayment).
 * Equivalent to `await getStripePromise()`.
 */
export async function getStripe() {
  return getStripePromise();
}

/**
 * Classification of a caught Stripe error, used to decide whether the message
 * is safe to surface verbatim and how to tag the failure in telemetry.
 */
export interface ClassifiedStripeError {
  /** Raw error message, or empty string when none is available. */
  message: string;
  /** Stripe.js itself failed to load, thrown by the wrappers in this module. */
  isLoadError: boolean;
  /** card_error / validation_error, message is from Stripe's localized catalog and safe to display verbatim. */
  isCardError: boolean;
  /** Stable category for trackFailure(..., { failure_type }). */
  failureType: "stripe_load" | "card_decline" | "stripe_unknown";
}

/**
 * Wraps a raw Stripe.js error (a plain object, not an Error instance — the
 * shape `elements.submit()` and `stripe.confirmSetup()`/`confirmPayment()`/
 * `confirmCardPayment()` reject with) into an Error carrying its type/code.
 * Centralises the wrap so every confirm* helper below hands classifyStripeError
 * (and any other `instanceof Error` check downstream) a consistent shape.
 */
function toClassifiableStripeError(stripeError: { message?: string; type?: string; code?: string }): Error {
  const err = new Error(stripeError.message ?? "Payment confirmation failed");
  Object.assign(err, { type: stripeError.type, code: stripeError.code });
  return err;
}

/**
 * Classify a caught Stripe error so callers can decide whether the message is
 * safe to display verbatim and how to tag the failure. Centralises logic that
 * was duplicated across the create-subscription, retry-payment and add-source flows.
 */
export function classifyStripeError(err: unknown): ClassifiedStripeError {
  // Defense in depth: every confirm* helper in this module now wraps Stripe's
  // raw error into an Error via toClassifiableStripeError, but read both
  // shapes here too so a future unwrapped throw doesn't silently regress into
  // a blank message and failure_type: "stripe_unknown" again (SPEAK-CLIENT-75
  // was exactly this — confirmSetupWithElements used to rethrow the raw
  // StripeError object, which `err instanceof Error` alone can't see).
  const errLike =
    err instanceof Error
      ? (err as Error & { type?: string })
      : err && typeof err === "object"
        ? (err as { message?: string; type?: string })
        : null;
  const message = errLike?.message ?? "";
  const isLoadError = message === "Stripe failed to load";
  const isCardError =
    !isLoadError &&
    (errLike?.type === "card_error" || errLike?.type === "validation_error");
  const failureType: ClassifiedStripeError["failureType"] = isLoadError
    ? "stripe_load"
    : isCardError
      ? "card_decline"
      : "stripe_unknown";
  return { message, isLoadError, isCardError, failureType };
}

/**
 * Confirm a Stripe payment using a client secret and payment method.
 * Returns the PaymentIntent result or throws on error.
 */
export async function confirmStripePayment(
  clientSecret: string,
  paymentMethodId: string,
  returnUrl?: string,
) {
  const stripe = await getStripe();
  if (!stripe) throw new Error("Stripe failed to load");

  const result = await stripe.confirmPayment({
    clientSecret,
    confirmParams: {
      payment_method: paymentMethodId,
      ...(returnUrl ? { return_url: returnUrl } : {}),
    },
    redirect: "if_required",
  });

  if (result.error) {
    // Preserve type + code on the thrown Error so callers can narrow by
    // Stripe error category (card_error, validation_error) before deciding
    // whether the message is safe to display verbatim. Previously rethrown
    // as a plain Error which dropped both fields, verbatim-message UX
    // never fired in production.
    throw toClassifiableStripeError(result.error);
  }

  return result.paymentIntent;
}

/**
 * Confirm a server-prepared PaymentIntent, pops the 3DS/SCA modal if required.
 * Use when the server has already attached the saved card to the PaymentIntent
 * (e.g. subscription invoice with `requiresAction === true`); the client only needs
 * to trigger the SCA challenge. Mirrors Angular's processStripePayment flow.
 */
export async function confirmStripeCardPayment(clientSecret: string) {
  const stripe = await getStripe();
  if (!stripe) throw new Error("Stripe failed to load");

  const result = await stripe.confirmCardPayment(clientSecret);

  if (result.error) {
    throw toClassifiableStripeError(result.error);
  }

  return result.paymentIntent;
}

/**
 * Confirm a setup intent using Stripe Elements. Saves a payment method without charging.
 * Used by AddSource when adding a card without immediate payment.
 *
 * @param params.stripe - The Stripe instance from useStripe() inside an <Elements> provider.
 * @param params.elements - The Stripe Elements instance from useElements().
 * @param params.clientSecret - The client secret from getSetupIntent.
 * @returns The SetupIntent on success; throws on error.
 */
export async function confirmSetupWithElements(params: {
  stripe: import("@stripe/stripe-js").Stripe;
  elements: import("@stripe/stripe-js").StripeElements;
  clientSecret: string;
}): Promise<import("@stripe/stripe-js").SetupIntent> {
  const { stripe, elements, clientSecret } = params;
  // Stripe Basil requires elements.submit() before any async work and confirmSetup().
  const { error: submitError } = await elements.submit();
  if (submitError) throw toClassifiableStripeError(submitError);
  const result = await stripe.confirmSetup({
    elements,
    clientSecret,
    redirect: "if_required",
    confirmParams: {
      return_url: typeof window !== "undefined" ? window.location.href : "",
    },
  });
  if (result.error) throw toClassifiableStripeError(result.error);
  if (!result.setupIntent) throw new Error("Stripe confirmSetup returned no setupIntent without an error");
  return result.setupIntent;
}
